import React, {useState, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {LanguageSelector} from '../../components/LanguageSelector/LanguageSelector';
import {getLoginUrl} from '../../UrlUtil';
import {SUPPORTED_URL_LOCALES, URL_DEFAULT_LOCALE, LOCALE_LOCALSTORAGE_KEY} from '../../translations/i18n';
import {AI_SUMMARY_MARKDOWN, AI_SUMMARY_HTML, AI_SUMMARY_JSON_LD, AI_SUMMARY_XML} from './aiSummary';
import './LandingPage.css';

import AbicartIcon from '../../logos/AbicartIcon.svg';
import AbicartWordmark from '../../logos/Abicart.svg';
import ClaudeLogo from '../../logos/Claude.svg';
import FennoaLogo from '../../logos/Fennoa.svg';
import FortnoxIcon from '../../logos/FortnoxIcon.svg';
import FortnoxWordmark from '../../logos/FortnoxWordmark.svg';
import GoogleBigQueryIcon from '../../logos/GoogleBigQueryIcon.svg';
import GoogleMapsIcon from '../../logos/GoogleMapsIcon.svg';
import LoopiaLogo from '../../logos/Loopia.svg';
import MSBusinessCentralLogo from '../../logos/MS-BusinessCentral.svg';
import OWSLogo from '../../logos/OWS.svg';
import SpirisLogo from '../../logos/Spiris.svg';
import TwilioLogo from '../../logos/Twilio.svg';
import VismaLogo from '../../logos/Visma.svg';
import WhatsappLogo from '../../logos/Whatsapp.svg';
import OneComLogo from '../../logos/one.com.svg';
import ResultatFinansLogo from '../../logos/resultat_finans_logo_70.png';
import WHATSAPP_GIF from '../../assets/whatsapp_transparent.gif';

const BRAND_LOGO = 'https://storage.googleapis.com/pifa.se/partihandel.se_row_inverted.png';
const WHATSAPP_URL = 'https://wa.me/+46700700739';
const WhatsAppCta = ({label, size = 'md', className = ''}) => (
	<a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={`whatsapp-cta whatsapp-cta--${size} ${className}`}>
		<span className="whatsapp-cta-icon">
			<img src={WHATSAPP_GIF} alt="" className="whatsapp-cta-img" loading="lazy" />
		</span>
		{label && <span className="whatsapp-cta-label">{label}</span>}
	</a>
);

const CUSTOMERS = [
	{href: 'https://atatrading.se', title: 'ATA Trading AB', img: 'https://storage.googleapis.com/pifa.se/AKA-02.png', bg: '#F2C647', scale: 1.4},
	{href: 'https://www.facebook.com/HelsingborgBazaar01/', title: 'Bazaar Grupp AB', img: 'https://storage.googleapis.com/pifa.se/bazaar.png', scale: 1.6},
	{href: 'https://ethfruktogront.se', title: 'ETH Frukt & Grönt', img: 'https://storage.googleapis.com/pifa.se/eth.png', bg: '#FFFFFF', scale: 1.3},
	{href: 'https://ggbfruit.se/', title: 'GGB Fruit Scandinavia AB', img: 'https://storage.googleapis.com/pifa.se/companies/672c9ef48ede72414ba1e2f1/logos/combinationMark/combinationmark.png', bg: '#fff', scale: 1.3},
	{href: 'https://fgcentralen.se', title: 'FG Centralen AB', img: 'https://storage.googleapis.com/pifa.se/fgc.png', bg: '#FFFFFF', scale: 1.2},
	{href: 'https://nordiskfrukt.se/', title: 'Nordisk Frukt AB', img: 'https://storage.googleapis.com/pifa.se/logos/nordiskfrukt.jpg', bg: '#FFFFFF', scale: 1.4},
	{href: 'https://vaasanvihannes.fi/', title: 'Oy Vaasan Vihannes - Vasa Grönsaker Ab', img: 'https://storage.googleapis.com/pifa.se/vasa.png', bg: '#FFFFFF', scale: 1.5},
	{href: 'https://lundgrensfruktochdelikatesser.se/', title: "Lundgren's Frukt & Delikatesser AB", img: 'https://storage.googleapis.com/pifa.se/companies/685abfef6f5ac9bb8bf67002/logos/wordMark/LFD_2.png', bg: '#FFFFFF', scale: 1.2},
	{href: 'https://mandarinfrukt.se', title: 'Mandarin Frukt & Grönt AB', img: 'https://storage.googleapis.com/pifa.se/mandarin.png', bg: '#FFFFFF', scale: 2.2},
	{href: 'https://fruktombudet.se', title: 'Fruktombudet', img: 'https://storage.googleapis.com/pifa.se/public/image-removebg-preview%20(11).png', bg: '#FFE2D6', scale: 1.1},
	{href: 'https://svenskasvampar.se', title: 'Mitson Bär & Svamp AB', img: 'https://storage.googleapis.com/pifa.se/companies/662b8680241c5b2e575ce5fa/logos/wordMark/121BCF2B-5008-47CF-8714-BAF7244188F2.png', bg: '#F2EBDF', scale: 1.7},
	{href: 'mailto:info@opfrukt.se', title: 'OP Frukt & Grönt AB', img: 'https://storage.googleapis.com/pifa.se/op.png', bg: '#FFFFFF', scale: 1.2},
	{href: 'https://gronsaksboden.se/', title: 'Grönsaksboden i Stockholm AB', img: 'https://storage.googleapis.com/pifa.se/companies/69dcc8fa8ffb23e1995a23a8/logos/wordMark/gboden.png', bg: '#FFFFFF', scale: 1.2},
	{href: 'https://lotusfoodservice.se', title: 'LFG Import & Export AB', img: 'https://storage.googleapis.com/pifa.se/companies/67cafa1d48c3bb8c98392d24/logos/wordMark/6983103_fullsize.png', bg: '#FFFFFF', scale: 1.2},
	{href: 'https://ludvikapartiaffar.se/', title: 'Ludvika Partiaffär AB', img: 'https://storage.googleapis.com/pifa.se/companies/66f02fbb19d6fe65265106ea/logos/wordmark/ludvika.png', bg: '#F7CC11', scale: 1.3},
	{href: 'https://nathaliefg.se/', title: 'Nathalie Frukt & Grönt AB', img: 'https://storage.googleapis.com/pifa.se/companies/690b4767017f1ce241cf35e1/logos/wordMark/nathaliefg.png', bg: '#061A2A', scale: 1.8},
	{href: 'https://vfwab.se', title: 'Vegetables & Fruits World AB', img: 'https://storage.googleapis.com/pifa.se/companies/68c266fef75b902ff183cb41/logos/wordMark/1000147495.png', bg: '#FFFFFF', scale: 1.5},
	{href: 'https://odlarlaget.se', title: 'Svenska Odlarlaget', img: 'https://storage.googleapis.com/pifa.se/companies/6617c990824414844797191b/logos/wordmark/odlarlaget_row.png', scale: 1.3},
	{href: 'https://arivofresh.se', title: 'Arivo Fresh AB', img: 'https://storage.googleapis.com/pifa.se/arivo.png', bg: '#FFFFFF', scale: 1.4},
	{href: 'https://www.amfrukt.se/e', title: 'AM Frukt AB', img: 'https://storage.googleapis.com/pifa.se/AMFRUKT.png', bg: '#C6CEC4', scale: 1.1},

	{href: 'https://ezmgross.se', title: 'EZM Grossist AB', img: 'https://storage.googleapis.com/pifa.se/companies/69327305c1ce776ef46c4f2f/logos/wordMark/ezmloggo1.png', bg: '#FFFFFF'},
	{href: 'https://farmersandchefs.se', title: 'Farmers & Chefs AB', img: 'https://storage.googleapis.com/pifa.se/companies/68da4c14ae5bfbe18ac234ca/logos/wordMark/Untitled%20drawing%20(9).png', bg: '#FFFFFF', scale: 1.4},
	{href: 'https://fruktlandet.se/', title: 'Frukt Landet i Norden AB', img: 'https://storage.googleapis.com/pifa.se/companies/691f0349b8757cca11ca8c4c/logos/wordMark/Fruktlandet_logo_HD.png', bg: '#FFFFFF', scale: 1.5},
	{href: 'https://marrakech.se', title: 'Cranberry Group AB', img: 'https://storage.googleapis.com/pifa.se/companies/61ebb69ff21bbbc43e6b5b8b/logos/combinationMark/1000214153.png', bg: '#FFFFFF'},
	{href: 'https://redalb.com.al', title: 'Red ALB Global AB', img: 'https://storage.googleapis.com/pifa.se/redalb.png', bg: '#FFFFFF', scale: 1.3},
	{href: 'https://www.resultatfinans.se/', title: 'Resultat Finans i Skandinavien AB', img: 'https://storage.googleapis.com/pifa.se/logos/rfinans.png', bg: '#FFFFFF'},
	{href: 'https://www.swecofoodservice.se/', title: 'SWECO Food Service AB', img: 'https://storage.googleapis.com/pifa.se/companies/67d5377da1213f3dfeb27872/logos/wordMark/download.png', bg: '#FFFFFF', scale: 1.3},
	{href: 'https://vivafresh.se', title: 'VI-VA World Food AB', img: 'https://storage.googleapis.com/pifa.se/image-removebg-preview%20(12).png', bg: '#E6E8EB', scale: 1.4},
];

const TiktokIcon = () => (
	<svg viewBox="0 0 50 50" fill="currentColor" aria-hidden="true">
		<path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z" />
	</svg>
);
const InstagramIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9A5.5 5.5 0 0 1 16.5 22h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9zm9.25 1.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
	</svg>
);
const GithubIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.111.82-.261.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
	</svg>
);
const XIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
	</svg>
);
const LinkedinIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5v-9h3v9zM6.5 8.25A1.75 1.75 0 1 1 8.3 6.5a1.78 1.78 0 0 1-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0 0 13 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 0 1 2.7-1.4c1.55 0 3.4.93 3.4 3.7V19z" />
	</svg>
);
const PinIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
	</svg>
);
const GlobeIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
	</svg>
);
const MailIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
	</svg>
);
const PhoneIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
	</svg>
);
const WhatsappIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M19.1 4.9C17.2 3 14.6 2 12 2 6.5 2 2 6.5 2 12c0 1.7.4 3.4 1.3 4.9L2 22l5.2-1.3c1.5.8 3.1 1.2 4.8 1.2 5.5 0 10-4.5 10-10 0-2.6-1-5.2-2.9-7zM12 20.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.1.8.8-3-.2-.3c-.8-1.4-1.3-2.9-1.3-4.4 0-4.5 3.7-8.2 8.2-8.2 2.2 0 4.3.9 5.8 2.4s2.4 3.6 2.4 5.8c.2 4.6-3.5 8.3-8 8.3zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.7.8-.8.9-.1.2-.3.2-.5.1-.7-.3-1.4-.7-2-1.2-.5-.5-1-1.1-1.4-1.7-.1-.2 0-.4.1-.5l.4-.4c.1-.1.2-.3.2-.4.1-.1.1-.3 0-.4 0-.1-.6-1.3-.7-1.8-.1-.7-.3-.7-.5-.7h-.5c-.2 0-.5.2-.6.3-.6.6-.9 1.3-.9 2.1.1.9.4 1.8 1 2.6 1.1 1.6 2.5 2.9 4.2 3.7.5.2.9.4 1.4.5.5.2 1 .2 1.6.1.7-.1 1.3-.6 1.7-1.2.2-.4.2-.8.1-1.2l-.5-.1z" />
	</svg>
);
const ArrowUpIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.59 5.58L20 12l-8-8-8 8z" />
	</svg>
);

// Source of truth: monthly volume in SEK. Update this single number and every
// locale's displayed amount derives from it via FX rate.
const MONTHLY_VOLUME_SEK = 200_000_000;

// Approximate FX rates from SEK to each supported currency. Update yearly if
// material moves shift the displayed numbers. Granularity-rounding below
// hides minor rate drift; only refresh when a rate moves by >~15%.
const FX_RATE_FROM_SEK = {
	SEK: 1,
	EUR: 0.087,
	DKK: 0.65,
	NOK: 1.02,
	PLN: 0.37,
	HUF: 33,
	ALL: 8.6,
	RSD: 10.1,
	MKD: 5.3,
	UAH: 3.7,
	RUB: 7.9,
	TRY: 3.0,
	CNY: 0.68,
};

// Locale → currency. Non-euro Europe maps to its native currency. Eurozone
// and any unlisted locale falls back to EUR.
const CURRENCY_BY_LOCALE = {
	sv: 'SEK',
	da: 'DKK',
	nb: 'NOK',
	pl: 'PLN',
	hu: 'HUF',
	sq: 'ALL',
	sr: 'RSD',
	mk: 'MKD',
	uk: 'UAH',
	ru: 'RUB',
	tr: 'TRY',
	zh: 'CNY',
};

// Round UP to the cleanest increment given the amount's magnitude. Always
// up so the displayed number stays honest against the "+" suffix.
function roundUpToCleanIncrement(amount) {
	if (amount >= 1_000_000_000) return Math.ceil(amount / 100_000_000) * 100_000_000;
	if (amount >= 100_000_000) return Math.ceil(amount / 10_000_000) * 10_000_000;
	return Math.ceil(amount / 1_000_000) * 1_000_000;
}

function localizeMonthlyVolume(stats, locale) {
	if (!Array.isArray(stats) || stats.length < 3) return stats;
	const lang = (locale || 'en').split('-')[0].toLowerCase();
	const currency = CURRENCY_BY_LOCALE[lang] || 'EUR';
	const rate = FX_RATE_FROM_SEK[currency] || FX_RATE_FROM_SEK.EUR;
	const amount = roundUpToCleanIncrement(MONTHLY_VOLUME_SEK * rate);
	let formatted;
	try {
		formatted = new Intl.NumberFormat(locale || 'en', {
			style: 'currency',
			currency,
			maximumFractionDigits: 0,
			minimumFractionDigits: 0,
		}).format(amount);
	} catch (e) {
		formatted = String(amount);
	}
	return stats.map((s, i) => (i === 2 ? {...s, num: `${formatted}+`} : s));
}

export default function LandingPage() {
	const {t, i18n} = useTranslation();
	const params = useParams();
	const [openFaq, setOpenFaq] = useState(0);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [scrolledPastHeroCta, setScrolledPastHero] = useState(false);

	const langBase = (i18n.language || '').split('-')[0];
	const isRTL = ['ar', 'ur', 'fa', 'ps', 'ckb'].includes(langBase);

	// URL → i18n sync + first-visit redirect.
	// Path-based locale wins; if user lands on / with no stored preference, we
	// detect navigator.language once and redirect to /<locale>/ when applicable.
	useEffect(() => {
		const pathLocale = params.locale && SUPPORTED_URL_LOCALES.includes(params.locale) ? params.locale : null;

		if (pathLocale) {
			if (i18n.language !== pathLocale) i18n.changeLanguage(pathLocale);
			try {
				localStorage.setItem(LOCALE_LOCALSTORAGE_KEY, pathLocale);
			} catch (e) {
				// localStorage can throw in private mode; non-critical.
			}
			return;
		}

		// We're at / (Swedish default).
		let stored = null;
		try {
			stored = localStorage.getItem(LOCALE_LOCALSTORAGE_KEY);
		} catch (e) {}

		if (stored && SUPPORTED_URL_LOCALES.includes(stored) && stored !== URL_DEFAULT_LOCALE) {
			window.location.replace(`/${stored}/`);
			return;
		}

		if (!stored) {
			const browser = (navigator.language || 'sv').slice(0, 2).toLowerCase();
			// Map deprecated "no" to "nb" — we only ship /nb/.
			const mapped = browser === 'no' ? 'nb' : browser;
			if (SUPPORTED_URL_LOCALES.includes(mapped) && mapped !== URL_DEFAULT_LOCALE) {
				try {
					localStorage.setItem(LOCALE_LOCALSTORAGE_KEY, mapped);
				} catch (e) {}
				window.location.replace(`/${mapped}/`);
				return;
			}
			// Browser language is Swedish or unsupported — stick on /.
			if (i18n.language !== URL_DEFAULT_LOCALE) i18n.changeLanguage(URL_DEFAULT_LOCALE);
		} else if (i18n.language !== URL_DEFAULT_LOCALE) {
			i18n.changeLanguage(URL_DEFAULT_LOCALE);
		}
	}, [params.locale, i18n]);

	useEffect(() => {
		document.title = t('landing.meta.title');
		document.documentElement.lang = i18n.language || 'en';
		const description = t('landing.meta.description');
		const setMeta = (selector, attr, value) => {
			const el = document.head.querySelector(selector);
			if (el) el.setAttribute(attr, value);
		};
		setMeta('meta[name="description"]', 'content', description);
		setMeta('meta[property="og:description"]', 'content', description);
		setMeta('meta[property="og:title"]', 'content', t('landing.meta.title'));
		setMeta('meta[name="twitter:description"]', 'content', description);
		setMeta('meta[name="twitter:title"]', 'content', t('landing.meta.title'));
	}, [t, i18n.language]);

	useEffect(() => {
		const target = document.querySelector('.lp .hero .whatsapp-cta');
		if (!target) return;
		const observer = new IntersectionObserver(([entry]) => setScrolledPastHero(!entry.isIntersecting), {threshold: 0, rootMargin: '0px 0px 0px 0px'});
		observer.observe(target);
		return () => observer.disconnect();
	}, []);

	// Measure actual nav height so the hero exactly fills (viewport - nav).
	// Hardcoded values drift as nav content changes; this stays accurate and
	// makes the stats ticker's bottom land precisely on the viewport edge.
	useEffect(() => {
		const nav = document.querySelector('.lp .nav');
		if (!nav) return;
		const update = () => {
			document.documentElement.style.setProperty('--nav-h', `${nav.offsetHeight}px`);
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(nav);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [mobileMenuOpen]);

	const stats = t('landing.stats', {returnObjects: true});
	const localizedStats = useMemo(() => localizeMonthlyVolume(stats, i18n.language), [stats, i18n.language]);
	const industries = t('landing.industries.items', {returnObjects: true});
	const companies = t('landing.companies.items', {returnObjects: true});
	const overviewIntro = t('landing.overviewIntro', {returnObjects: true, defaultValue: []});
	const overviewIntroParagraphs = Array.isArray(overviewIntro) ? overviewIntro : [overviewIntro].filter(Boolean);
	const modules = t('landing.modules', {returnObjects: true});
	const specs = t('landing.platform.specs', {returnObjects: true});
	const faqs = t('landing.faqs', {returnObjects: true});

	return (
		<div className="lp">
			{/* ============ NAV ============ */}
			<div className={`nav ${scrolledPastHeroCta ? 'nav--scrolled' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
				<a href="/" className="brand">
					<img src={BRAND_LOGO} alt="Partihandel" width="5802" height="1139" />
				</a>
				<WhatsAppCta size="md" label={t('landing.whatsapp.label')} className="nav-cta--brand" />
				<ul className="nav-links">
					<li>
						<a href="#functionality">{t('landing.nav.functionality')}</a>
					</li>
					<li>
						<a href="#integrations">{t('landing.nav.integrations')}</a>
					</li>
					<li>
						<a href="#platform">{t('landing.nav.platform')}</a>
					</li>
					<li>
						<a href="#customers">{t('landing.nav.customerReferences')}</a>
					</li>
					<li>
						<a href="#faq">{t('landing.nav.faq')}</a>
					</li>
					<li>
						<a href="#contact">{t('landing.nav.contact')}</a>
					</li>
				</ul>
				<div className="nav-right">
					<LanguageSelector color="var(--black)" label={t('landing.nav.language')} />
					<a href={getLoginUrl()} className="nav-icon-btn">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
							<polyline points="10 17 15 12 10 7" />
							<line x1="15" y1="12" x2="3" y2="12" />
						</svg>
						<span className="nav-icon-btn-label">{t('landing.nav.login')}</span>
					</a>
					<button type="button" className="nav-icon-btn nav-hamburger" onClick={() => setMobileMenuOpen(true)} aria-label={t('landing.nav.openMenu')}>
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
							<line x1="4" y1="7" x2="20" y2="7" />
							<line x1="4" y1="12" x2="20" y2="12" />
							<line x1="4" y1="17" x2="20" y2="17" />
						</svg>
						<span className="nav-icon-btn-label">{t('landing.nav.menu')}</span>
					</button>
				</div>
			</div>

			{/* ============ MOBILE MENU DRAWER ============ */}
			{mobileMenuOpen && (
				<div className="mobile-menu" dir={isRTL ? 'rtl' : 'ltr'}>
					<div className="mobile-menu-header">
						<a href="/" className="brand" onClick={() => setMobileMenuOpen(false)}>
							<img src={BRAND_LOGO} alt="Partihandel" />
						</a>
						<button type="button" className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)} aria-label={t('landing.nav.closeMenu')}>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
								<line x1="5" y1="5" x2="19" y2="19" />
								<line x1="19" y1="5" x2="5" y2="19" />
							</svg>
						</button>
					</div>
					<nav className="mobile-menu-nav">
						<a href="#functionality" onClick={() => setMobileMenuOpen(false)}>
							{t('landing.nav.functionality')}
						</a>
						<a href="#integrations" onClick={() => setMobileMenuOpen(false)}>
							{t('landing.nav.integrations')}
						</a>
						<a href="#platform" onClick={() => setMobileMenuOpen(false)}>
							{t('landing.nav.platform')}
						</a>
						<a href="#customers" onClick={() => setMobileMenuOpen(false)}>
							{t('landing.nav.customerReferences')}
						</a>
						<a href="#faq" onClick={() => setMobileMenuOpen(false)}>
							{t('landing.nav.faq')}
						</a>
						<a href="#contact" onClick={() => setMobileMenuOpen(false)}>
							{t('landing.nav.contact')}
						</a>
					</nav>
					<WhatsAppCta size="lg" label={t('landing.whatsapp.label')} className="mobile-menu-cta" />
					<a href="https://calendly.com/partihandel" className="mobile-menu-secondary" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
						{t('landing.nav.bookDemo')} &rarr;
					</a>
				</div>
			)}

			{/* ============ HERO ============ */}
			<section className="hero" dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="wrap">
					<h1 className="hero-h1-sr">{t('landing.meta.h1')}</h1>
					<p className="hero-headline">
						{(() => {
							const title = t('landing.hero.title');
							const i = title.indexOf(' ');
							if (i < 0) return title;
							return (
								<>
									<span className="hero-think">{title.slice(0, i + 1)}</span>
									<span className="hero-big">{title.slice(i + 1)}</span>
								</>
							);
						})()}
					</p>
					<h2>{t('landing.hero.subtitle')}</h2>
					<p>
						{t('landing.hero.bodyLine1')}
						<br />
						{t('landing.hero.bodyLine2')}
					</p>
					<div className="hero-cta">
						<WhatsAppCta size="lg" label={t('landing.hero.whatsapp')} />
						<a href="#functionality" className="btn">
							{t('landing.hero.seeWhatItDoes')}
						</a>
						<a href="https://calendly.com/partihandel" className="btn btn-ghost" target="_blank" rel="noopener noreferrer">
							{t('landing.hero.bookDemo')}
						</a>
					</div>
				</div>
			</section>

			{/* ============ TICKER · LIVE STATS (below the fold) ============ */}
			<div className="ticker hero-ticker" dir={isRTL ? 'rtl' : 'ltr'}>
				{localizedStats.map((s, i) => (
					<React.Fragment key={i}>
						{i > 0 && <span className="sep">·</span>}
						<span className="stat">
							<span className="num">{s.num}</span> {s.text}
						</span>
					</React.Fragment>
				))}
			</div>

			{/* ============ TICKER · INDUSTRIES ============ */}
			<div className="ticker industries-ticker" dir={isRTL ? 'rtl' : 'ltr'}>
				<span className="label">{t('landing.industries.label')}</span>
				{industries.map((item, i) => (
					<React.Fragment key={item}>
						{i > 0 && <span className="sep">·</span>}
						<span className="stat">{item}</span>
					</React.Fragment>
				))}
			</div>

			{/* ============ TICKER · COMPANIES ============ */}
			<div className="ticker companies-ticker" dir={isRTL ? 'rtl' : 'ltr'}>
				<span className="label">{t('landing.companies.label')}</span>
				{companies.map((item, i) => (
					<React.Fragment key={item}>
						{i > 0 && <span className="sep">·</span>}
						<span className="stat">{item}</span>
					</React.Fragment>
				))}
			</div>

			{/* ============ EXECUTIVE SUMMARY ============ */}
			<section className="panel" id="overview" aria-label={t('landing.panels.overview')} dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="wrap">
					<h2 className="sr-only">{t('landing.panels.overview')}</h2>
					<div className="twocol">
						<div>
							{overviewIntroParagraphs.map((p, i) => (
								<p key={i}>{p}</p>
							))}
							<p className="intro-cta-line">
								{t('landing.introCta.before')}
								<a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="intro-cta-link">
									{t('landing.introCta.linkText')}
								</a>
								{t('landing.introCta.after')}
							</p>
						</div>
					</div>

					{/* AI summary - hidden from human visitors but indexable by bots,
					    crawlers, LLMs and agents. All four formats present in the DOM. */}
					<div className="ai-summary-data" aria-hidden="true">
						<pre data-format="markdown">{AI_SUMMARY_MARKDOWN}</pre>
						<div data-format="html" dangerouslySetInnerHTML={{__html: AI_SUMMARY_HTML}} />
						<pre data-format="json-ld">{AI_SUMMARY_JSON_LD}</pre>
						<pre data-format="xml">{AI_SUMMARY_XML}</pre>
					</div>
					<script type="application/ld+json" dangerouslySetInnerHTML={{__html: AI_SUMMARY_JSON_LD}} />
				</div>
			</section>

			{/* ============ INTEGRATIONS ============ */}
			<section className="panel" id="integrations" dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="wrap">
					<div className="panel-label">
						<span className="num">01</span>
						<h2 className="title">{t('landing.panels.integrations')}</h2>
						<span className="rule"></span>
					</div>

					<div className="twocol" style={{marginBottom: 32}}>
						<div>
							{t('landing.integrationsIntro')
								.split('\n\n')
								.map((para, i) => (
									<p key={i}>{para}</p>
								))}
						</div>
					</div>

					<div className="integrations">
						<div className="integrations-group integrations-group--lg">
							<div className="integrations-tiles">
								<div className="integration-tile integration-tile--bg-fennoa" title="Fennoa">
									<img src={FennoaLogo} alt="Fennoa" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--fortnox integration-tile--bg-fortnox" title="Fortnox">
									<img src={FortnoxIcon} alt="" className="ft-icon" loading="lazy" />
									<img src={FortnoxWordmark} alt="Fortnox" className="ft-wordmark" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-msbc integration-tile--scale-115" title="Microsoft Business Central">
									<img src={MSBusinessCentralLogo} alt="Microsoft Business Central" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-spiris integration-tile--spiris-only" title="Spiris">
									<img src={SpirisLogo} alt="Spiris" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-spiris" title="Visma">
									<img src={VismaLogo} alt="Visma" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-dark integration-tile--scale-115" title="WhatsApp">
									<img src={WhatsappLogo} alt="WhatsApp" loading="lazy" />
								</div>
							</div>
						</div>
						<div className="integrations-group integrations-group--md">
							<div className="integrations-tiles">
								<div className="integration-tile integration-tile--bg-abicart integration-tile--abicart" title="Abicart">
									<img src={AbicartIcon} alt="" className="ab-icon" loading="lazy" />
									<img src={AbicartWordmark} alt="Abicart" className="ab-wordmark" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-google integration-tile--groute" title="Google BigQuery">
									<img src={GoogleBigQueryIcon} alt="Google BigQuery" className="gr-mapsicon" loading="lazy" />
									<span className="gr-label">
										Google
										<br />
										BigQuery
									</span>
								</div>
								<div className="integration-tile integration-tile--bg-google integration-tile--groute" title="Google Maps Platform API">
									<img src={GoogleMapsIcon} alt="Google Maps Platform API" className="gr-mapsicon" loading="lazy" />
									<span className="gr-label">
										Google Maps
										<br />
										Platform API
									</span>
								</div>
								<div className="integration-tile integration-tile--bg-ows integration-tile--scale-85" title="OWS">
									<img src={OWSLogo} alt="OWS" loading="lazy" />
								</div>
							</div>
						</div>
						<div className="integrations-group">
							<div className="integrations-tiles">
								<div className="integration-tile integration-tile--bg-claude" title="Claude">
									<img src={ClaudeLogo} alt="Claude" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-loopia" title="Loopia">
									<img src={LoopiaLogo} alt="Loopia" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-onecom" title="one.com">
									<img src={OneComLogo} alt="one.com" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-rf" title="Resultat Finans">
									<img src={ResultatFinansLogo} alt="Resultat Finans" loading="lazy" />
								</div>
								<div className="integration-tile integration-tile--bg-twilio" title="Twilio">
									<img src={TwilioLogo} alt="Twilio" loading="lazy" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ============ CUSTOMER LOGOS ============ */}
			<section className="logos" id="customers" dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="panel-label">
					<span className="num">02</span>
					<h2 className="title">{t('landing.customersTitle')}</h2>
					<span className="rule"></span>
				</div>
				<div className="twocol" style={{marginBottom: 32}}>
					<div>
						<p>{t('landing.customersIntro')}</p>
					</div>
				</div>
				<div className="logos-grid">
					{CUSTOMERS.map(c => (
						<a key={c.title} href={c.href} target="_blank" rel="noopener noreferrer" className="logo-tile" title={c.title} style={c.bg ? {background: c.bg, borderColor: 'transparent'} : undefined}>
							<img src={c.img} alt={c.title} loading="lazy" style={c.scale ? {transform: `scale(${c.scale})`} : undefined} />
						</a>
					))}
				</div>
			</section>

			{/* ============ MODULES TABLE ============ */}
			<section className="panel" id="functionality" dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="wrap">
					<div className="panel-label">
						<span className="num">03</span>
						<h2 className="title">{t('landing.panels.functionality')}</h2>
						<span className="rule"></span>
					</div>
					<div className="twocol" style={{marginBottom: 32}}>
						<div>
							<p>{t('landing.functionalityIntro')}</p>
						</div>
					</div>
					<div className="modules">
						{modules.map(m => (
							<article key={m.title} className="module-card">
								<h3>{m.title}</h3>
								<ul>
									{m.items.map(item => (
										<li key={item}>{item}</li>
									))}
								</ul>
							</article>
						))}
					</div>
				</div>
			</section>

			{/* ============ SYSTEM / SPECS ============ */}
			<section className="panel" id="platform" dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="wrap">
					<div className="panel-label">
						<span className="num">04</span>
						<h2 className="title">{t('landing.panels.platform')}</h2>
						<span className="rule"></span>
					</div>

					<div className="twocol" style={{marginBottom: 32}}>
						<div>
							<p>{t('landing.platform.intro1')}</p>
						</div>
					</div>

					<div className="kv">
						{specs.map(s => (
							<React.Fragment key={s.k}>
								<div className="k">{s.k}</div>
								<div className="v">{s.v}</div>
							</React.Fragment>
						))}
					</div>
				</div>
			</section>

			{/* ============ FAQ ============ */}
			<section className="panel" id="faq" dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="wrap">
					<div className="panel-label">
						<span className="num">05</span>
						<h2 className="title">{t('landing.panels.faq')}</h2>
						<span className="rule"></span>
					</div>

					<div className="twocol" style={{marginBottom: 32}}>
						<div>
							<p>{t('landing.faqIntro')}</p>
						</div>
					</div>

					<div className="faq">
						{faqs.map((item, i) => (
							<div key={item.q} className={`faq-item${openFaq === i ? ' open' : ''}`}>
								<button type="button" className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
									{item.q}
								</button>
								<div className="faq-a">{item.a}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ============ CTA STRIP ============ */}
			<section className="cta-strip" id="contact" dir={isRTL ? 'rtl' : 'ltr'}>
				<h2>{t('landing.ctaStrip.title')}</h2>
				<p>{t('landing.ctaStrip.body')}</p>
				<div className="cta-strip-actions">
					<WhatsAppCta size="lg" label={t('landing.whatsapp.label')} />
					<a href="https://calendly.com/partihandel" className="cta-pill cta-pill--lg" target="_blank" rel="noopener noreferrer">
						{t('landing.ctaStrip.button')}
					</a>
				</div>
			</section>

			{/* ============ FOOTER ============ */}
			<footer className="site-footer" dir={isRTL ? 'rtl' : 'ltr'}>
				<div className="footer-inner">
					<div className="footer-top">
						<a href="/" className="footer-brand">
							<img src={BRAND_LOGO} alt="Partihandel" width="5802" height="1139" />
						</a>

						<div className="footer-social">
							<div className="footer-social-title">{t('landing.footer.followUs')}</div>
							<div className="footer-social-row">
								<a href="https://tiktok.com/@partihandel" className="footer-social-link" target="_blank" rel="noopener noreferrer">
									<TiktokIcon />
									<span>TikTok</span>
								</a>
								<a href="https://instagram.com/partihandel.se" className="footer-social-link" target="_blank" rel="noopener noreferrer">
									<InstagramIcon />
									<span>Instagram</span>
								</a>
								<a href="https://github.com/pifa-group" className="footer-social-link" target="_blank" rel="noopener noreferrer">
									<GithubIcon />
									<span>GitHub</span>
								</a>
								<a href="https://twitter.com/partihandel" className="footer-social-link" target="_blank" rel="noopener noreferrer">
									<XIcon />
									<span>X</span>
								</a>
								<a href="https://www.linkedin.com/company/partihanteraren/" className="footer-social-link" target="_blank" rel="noopener noreferrer">
									<LinkedinIcon />
									<span>LinkedIn</span>
								</a>
							</div>
						</div>
					</div>

					<div className="footer-cols" dir={isRTL ? 'rtl' : 'ltr'}>
						<div className="footer-col">
							<h4 className="footer-col-title">{t('landing.footer.aboutTitle')}</h4>
							<div className="footer-item">{t('landing.footer.companyName')}</div>
							<div className="footer-item">{t('landing.footer.regNo')}</div>
							<div className="footer-item">{t('landing.footer.vatNo')}</div>
							<div className="footer-item">{t('landing.footer.ftax')}</div>
							<a className="footer-item footer-item--icon" href="https://maps.app.goo.gl/C4t9phGxntPN682C9" target="_blank" rel="noopener noreferrer">
								<PinIcon />
								<span>{t('landing.footer.address')}</span>
							</a>
							<a className="footer-item footer-item--icon" href="https://partihandel.se" target="_blank" rel="noopener noreferrer">
								<GlobeIcon />
								<span>{t('landing.footer.website')}</span>
							</a>
						</div>

						<div className="footer-col">
							<h4 className="footer-col-title">{t('landing.footer.contactTitle')}</h4>
							<a className="footer-item footer-item--icon" href="mailto:info@partihandel.se">
								<MailIcon />
								<span>info@partihandel.se</span>
							</a>
							<a className="footer-item footer-item--icon" href="tel:+46700400450">
								<PhoneIcon />
								<span>+46 700 400 450</span>
							</a>
							<a className="footer-item footer-item--icon" href="https://wa.me/46700700739" target="_blank" rel="noopener noreferrer">
								<WhatsappIcon />
								<span>{t('landing.footer.contact.jeremias')}</span>
							</a>
							<a className="footer-item footer-item--icon" href="https://wa.me/46700700743" target="_blank" rel="noopener noreferrer">
								<WhatsappIcon />
								<span>{t('landing.footer.contact.tobias')}</span>
							</a>
							<a className="footer-item footer-item--icon" href="https://wa.me/46700700307" target="_blank" rel="noopener noreferrer">
								<WhatsappIcon />
								<span>{t('landing.footer.contact.marcus')}</span>
							</a>
						</div>

						<div className="footer-col">
							<h4 className="footer-col-title">{t('landing.footer.resourcesTitle')}</h4>
							<a className="footer-item" href="#functionality">
								{t('landing.footer.linkFunctionality')}
							</a>
							<a className="footer-item" href="#platform">
								{t('landing.footer.linkPlatform')}
							</a>
							<a className="footer-item" href="#faq">
								{t('landing.footer.linkFaq')}
							</a>
							<a className="footer-item" href="https://calendly.com/partihandel" target="_blank" rel="noopener noreferrer">
								{t('landing.footer.linkBookDemo')}
							</a>
							<a className="footer-item footer-item--legal" href="/gdpr">
								{t('landing.footer.linkPrivacy')}
							</a>
							<a className="footer-item" href="/cookies">
								{t('landing.footer.linkCookies')}
							</a>
						</div>
					</div>
				</div>

				<div className="footer-band" dir={isRTL ? 'rtl' : 'ltr'}>
					<button type="button" className="footer-totop" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
						<ArrowUpIcon />
						<span>{t('landing.footer.backToTop')}</span>
					</button>
					<div className="footer-colophon">
						<div className="lead">{t('landing.footer.colophon')}</div>
						<div className="copy">{t('landing.footer.copy')}</div>
					</div>
				</div>
			</footer>
		</div>
	);
}
